/**
 * SRES · Section Studio — Phase 2 · Feb 2026.
 *
 * Interactive 2-D cross-section workspace. CPs can:
 *   · Create a new section by choosing azimuth, origin (E/N), length, thickness
 *   · Preview the rendered section on the right
 *   · Save named sections to the project's section library
 *   · Bundle every saved section into a single PDF atlas (feeds into
 *     the Extended CPR "Section Atlas" appendix automatically).
 *
 * Backend endpoints:
 *   GET    /api/projects/{pid}/sections-studio/list
 *   POST   /api/projects/{pid}/sections-studio
 *   GET    /api/projects/{pid}/sections-studio/{sid}/render.png
 *   GET    /api/projects/{pid}/sections-studio/atlas.pdf
 *   DELETE /api/projects/{pid}/sections-studio/{sid}
 */
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus, DownloadSimple, Trash, ArrowsClockwise, MapTrifold, Compass,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";


function SectionCard({ section, isSelected, onSelect, onDelete }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 border transition-colors group ${
        isSelected
          ? "border-[#C99A3B] bg-[#0B2545] text-white"
          : "border-[#0B2545]/20 bg-white hover:border-[#0B2545]/50"
      }`}
      data-testid={`section-card-${section.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className={`text-[13px] font-semibold truncate ${
            isSelected ? "text-white" : "text-[#0B2545]"
          }`}>
            {section.name}
          </div>
          <div className={`text-[10.5px] font-mono ${
            isSelected ? "text-[#C99A3B]" : "text-slate-500"
          }`}>
            Az {String(Math.round(section.azimuth_deg) % 360).padStart(3, "0")}° ·
            E {section.origin_east.toFixed(0)} · N {section.origin_north.toFixed(0)}
          </div>
        </div>
        {onDelete ? (
          <span
            onClick={(e) => { e.stopPropagation(); onDelete(section.id); }}
            className={`shrink-0 rounded p-1 opacity-0 group-hover:opacity-100 ${
              isSelected ? "hover:bg-white/20" : "hover:bg-rose-100"
            }`}
            data-testid={`section-delete-${section.id}`}
          >
            <Trash size={12} className={isSelected ? "text-white" : "text-rose-600"} />
          </span>
        ) : null}
      </div>
    </button>
  );
}


function CreateSectionForm({ projectId, onCreated }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "Section 001",
    azimuth_deg: 90,
    origin_east: 0,
    origin_north: 0,
    length_m: 800,
    thickness_m: 25,
    display: "holes_blocks",
  });

  const seedFromBoundsQ = useQuery({
    queryKey: ["section-bounds", projectId],
    queryFn: () =>
      api
        .post(`/projects/${projectId}/section`, {
          orientation: "ew", position: 0, thickness: 25,
        })
        .catch(() => ({ data: null }))
        .then(() =>
          api.get(`/projects/${projectId}/section/bounds`).then((r) => r.data),
        ),
    enabled: !!projectId,
  });

  // Seed origin to the median collar position on first successful fetch.
  useMemo(() => {
    if (seedFromBoundsQ.data && form.origin_east === 0 && form.origin_north === 0) {
      const b = seedFromBoundsQ.data;
      setForm((f) => ({
        ...f,
        origin_east:  Math.round(b.x?.median ?? 0),
        origin_north: Math.round(b.y?.median ?? 0),
        length_m:     Math.round(Math.max(300, (b.x?.max ?? 0) - (b.x?.min ?? 0))),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot seeding only
  }, [seedFromBoundsQ.data]);

  const create = useMutation({
    mutationFn: (body) =>
      api.post(`/projects/${projectId}/sections-studio`, body).then((r) => r.data),
    onSuccess: (doc) => {
      toast.success(`Section "${doc.name}" saved`);
      qc.invalidateQueries({ queryKey: ["section-studio", projectId] });
      onCreated?.(doc);
    },
    onError: (e) => {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    },
  });

  const num = (k, step = "1") => (
    <Input
      type="number"
      step={step}
      value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
      className="rounded-none border-[#0B2545]/30 focus:border-[#C99A3B] font-mono text-[13px]"
      data-testid={`create-section-${k.replace(/_/g, "-")}`}
    />
  );

  return (
    <div className="border border-[#0B2545]/25 bg-[#F7F2E7]/40 p-4 space-y-3"
         data-testid="create-section-form">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#0B2545] flex items-center gap-2">
        <Compass size={12} className="text-[#C99A3B]" />
        Create new section
      </div>
      <div className="space-y-2">
        <div>
          <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-none border-[#0B2545]/30 focus:border-[#C99A3B] text-[13px]"
            data-testid="create-section-name"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Azimuth °</Label>
            {num("azimuth_deg")}
          </div>
          <div>
            <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Thickness m</Label>
            {num("thickness_m")}
          </div>
          <div>
            <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Origin E</Label>
            {num("origin_east", "0.1")}
          </div>
          <div>
            <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Origin N</Label>
            {num("origin_north", "0.1")}
          </div>
          <div className="col-span-2">
            <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Length m</Label>
            {num("length_m")}
          </div>
          <div className="col-span-2">
            <Label className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">Display</Label>
            <select
              value={form.display}
              onChange={(e) => setForm({ ...form, display: e.target.value })}
              className="w-full h-9 rounded-none border border-[#0B2545]/30 bg-white px-2 text-[13px] focus:border-[#C99A3B] focus:outline-none"
              data-testid="create-section-display"
            >
              <option value="holes_blocks">Holes + blocks</option>
              <option value="holes_only">Holes only</option>
              <option value="blocks_only">Blocks only</option>
            </select>
          </div>
        </div>
      </div>
      <Button
        type="button"
        onClick={() => create.mutate(form)}
        disabled={create.isPending || !form.name.trim()}
        className="w-full rounded-none bg-[#0B2545] hover:bg-[#0B2545]/90 text-white font-semibold"
        data-testid="create-section-submit"
      >
        {create.isPending ? "Saving…" : (
          <>
            <Plus size={12} className="mr-1" />
            Save section
          </>
        )}
      </Button>
    </div>
  );
}


export default function SectionStudioPage() {
  const { pid } = useParams();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [previewNonce, setPreviewNonce] = useState(0);   // bust image cache

  const listQ = useQuery({
    queryKey: ["section-studio", pid],
    queryFn: () =>
      api.get(`/projects/${pid}/sections-studio/list`).then((r) => r.data),
    enabled: !!pid,
  });

  const sections = listQ.data?.sections || [];
  const selected = sections.find((s) => s.id === selectedId) || sections[0];

  // Fetch the section PNG as a blob (bearer auth doesn't work on raw
  // <img> tags) and materialise it as an object URL.
  const previewQ = useQuery({
    queryKey: ["section-preview", pid, selected?.id, previewNonce],
    queryFn: async () => {
      if (!selected) return null;
      const res = await api.get(
        `/projects/${pid}/sections-studio/${selected.id}/render.png`,
        { responseType: "blob" },
      );
      return URL.createObjectURL(res.data);
    },
    enabled: !!selected,
    staleTime: 60_000,
  });

  const del = useMutation({
    mutationFn: (sid) =>
      api.delete(`/projects/${pid}/sections-studio/${sid}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Section deleted");
      qc.invalidateQueries({ queryKey: ["section-studio", pid] });
    },
  });

  const bundleAtlas = async () => {
    try {
      const res = await api.get(
        `/projects/${pid}/sections-studio/atlas.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "SectionAtlas.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        `Atlas download failed: ${e.response?.status === 404 ? "no saved sections" : e.message}`,
      );
    }
  };

  return (
    <div className="h-full overflow-hidden flex flex-col bg-white" data-testid="section-studio">
      {/* Header — gold + navy */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#0B2545] via-[#0E2E52] to-[#061735] px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MapTrifold size={22} weight="duotone" className="text-[#C99A3B]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C99A3B]">
                SRES Premium · Cross-Section Workshop
              </div>
              <h1 className="text-xl font-black text-white tracking-tight leading-tight">
                Section Studio
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); bundleAtlas(); }}
              className="inline-flex items-center gap-1.5 rounded-none border border-[#C99A3B]/60 bg-[#C99A3B]/10 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-widest text-[#F7F2E7] hover:bg-[#C99A3B]/20 hover:border-[#C99A3B] transition-colors"
              data-testid="section-studio-atlas-download"
            >
              <DownloadSimple size={12} weight="bold" />
              Download atlas PDF
            </a>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[340px_1fr]">
        {/* Left rail — form + list */}
        <div className="border-r border-[#0B2545]/15 overflow-y-auto p-4 space-y-4 bg-[#F7F2E7]/25">
          <CreateSectionForm projectId={pid} onCreated={(d) => setSelectedId(d.id)} />

          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#0B2545] mb-2 flex items-center justify-between">
              <span>Saved sections</span>
              <span className="text-slate-500 normal-case font-mono">
                {sections.length}
              </span>
            </div>
            {listQ.isLoading ? (
              <div className="text-[12px] text-slate-500">Loading…</div>
            ) : sections.length === 0 ? (
              <div className="text-[12px] text-slate-500 italic">
                No sections yet — create your first above.
              </div>
            ) : (
              <div className="space-y-1.5">
                {sections.map((s) => (
                  <SectionCard
                    key={s.id}
                    section={s}
                    isSelected={selected?.id === s.id}
                    onSelect={() => setSelectedId(s.id)}
                    onDelete={(sid) => del.mutate(sid)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right pane — section preview */}
        <div className="overflow-auto p-6 bg-[#F7F2E7]/10">
          {selected ? (
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545]">
                    Preview · rendered on the backend
                  </div>
                  <h2 className="text-lg font-bold text-[#0B2545]">
                    {selected.name}
                  </h2>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPreviewNonce((v) => v + 1)}
                  className="rounded-none border-[#0B2545]/30 text-[#0B2545]"
                  data-testid="section-studio-reload"
                >
                  <ArrowsClockwise size={12} className="mr-1" />
                  Regenerate
                </Button>
              </div>
              <div className="border border-[#0B2545]/25 bg-white p-2 shadow-sm min-h-[300px] flex items-center justify-center">
                {previewQ.isLoading ? (
                  <div className="flex flex-col items-center gap-2 text-slate-500 text-[12px]">
                    <ArrowsClockwise size={22} weight="duotone" className="animate-spin text-[#C99A3B]" />
                    Rendering section on the backend…
                  </div>
                ) : previewQ.isError ? (
                  <div className="text-rose-600 text-[13px] py-6">
                    Preview failed: {previewQ.error?.response?.data?.detail || previewQ.error?.message}
                  </div>
                ) : previewQ.data ? (
                  <img
                    src={previewQ.data}
                    alt={`Section ${selected.name}`}
                    className="w-full h-auto"
                    data-testid="section-studio-preview"
                    loading="eager"
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MapTrifold size={64} weight="duotone" className="text-[#C99A3B]/40 mx-auto mb-3" />
                <div className="text-[13px]">
                  Create your first section from the left panel.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
